import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { reportsAPI } from '../../services/api';

interface AgentProductivityWorkloadProps {
  startDate: string;
  endDate: string;
}

const AgentProductivityWorkload: React.FC<AgentProductivityWorkloadProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    fetchAgentProductivity();
  }, [startDate, endDate]);

  const fetchAgentProductivity = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await reportsAPI.getAgentReport({
        start_date: startDate,
        end_date: endDate
      });

      const agentList = (response.agents || []).map((agent: any) => {
        const resolutionRate = agent.conversations_handled > 0
          ? (agent.resolution_count / agent.conversations_handled) * 100
          : 0;

        const csatPercentage = (agent.csat_score / 5.0) * 100;

        const productivity_score = Math.round(
          (Math.min(agent.conversations_handled, 100) * 0.4) +
          (resolutionRate * 0.3) +
          (csatPercentage * 0.3)
        );

        const maxConversations = 150;
        const workload = Math.min((agent.conversations_handled / maxConversations) * 100, 100);

        const trend = resolutionRate > 70 ? 'up' : 'down';

        return {
          id: agent.agent_id,
          name: agent.agent_name,
          conversations: agent.conversations_handled,
          messages: agent.messages_sent,
          avg_handle_time: agent.avg_response_time,
          productivity_score,
          workload: Math.round(workload),
          trend
        };
      });

      setData({
        agents: agentList,
        avg_productivity: agentList.length > 0
          ? agentList.reduce((sum: number, a: any) => sum + a.productivity_score, 0) / agentList.length
          : 0,
        total_conversations: agentList.reduce((sum: number, a: any) => sum + a.conversations, 0),
        workload_distribution: agentList.map((a: any) => ({ name: a.name, workload: a.workload }))
      });
    } catch (err: any) {
      console.error('Failed to fetch agent productivity:', err);
      setError(err.message || 'Failed to load agent productivity data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data || !data.agents || data.agents.length === 0) {
    return (
      <Alert>
        <AlertDescription>No productivity data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const workloadChartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    dataLabels: { enabled: true, formatter: (val) => `${val}%` },
    xaxis: { categories: data.workload_distribution.map((d: any) => d.name), max: 100 },
    colors: ['#1f93ff']
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Avg Productivity Score</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.avg_productivity.toFixed(1)}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Across all agents</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Total Conversations</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.total_conversations.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">In selected period</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Active Agents</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.agents.length}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">With activity</span>
          </CardFooter>
        </Card>
      </div>

      {/* Workload Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Workload Distribution</CardTitle>
          <CardDescription>Normalized workload per agent as a percentage of maximum capacity</CardDescription>
        </CardHeader>
        <CardContent>
          <Chart
            options={workloadChartOptions}
            series={[{ name: 'Workload', data: data.workload_distribution.map((d: any) => d.workload) }]}
            type="bar"
            height={250}
          />
        </CardContent>
      </Card>

      {/* Agent Performance Details Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Agent Performance Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Conversations</TableHead>
                <TableHead className="text-right">Messages</TableHead>
                <TableHead className="text-right">Avg Handle Time</TableHead>
                <TableHead className="text-right">Productivity Score</TableHead>
                <TableHead className="text-center">Workload</TableHead>
                <TableHead className="text-center">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.agents.map((agent: any) => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">{agent.name[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{agent.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{agent.conversations}</TableCell>
                  <TableCell className="text-right">{agent.messages}</TableCell>
                  <TableCell className="text-right">{agent.avg_handle_time} min</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={agent.productivity_score >= 85 ? 'secondary' : 'outline'}>
                      {agent.productivity_score}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">{agent.workload}%</p>
                      <Progress value={agent.workload} className="h-1.5 w-20 mx-auto" />
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {agent.trend === 'up'
                      ? <TrendingUp className="h-4 w-4 text-muted-foreground mx-auto" />
                      : <TrendingDown className="h-4 w-4 text-muted-foreground mx-auto" />
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Productivity Insights */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Productivity Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-4 space-y-1">
            <li>
              <span className="text-xs text-muted-foreground">
                Productivity score considers: conversations handled, resolution rate, CSAT, and handle time
              </span>
            </li>
            <li>
              <span className="text-xs text-muted-foreground">
                Optimal workload: 70–85% for sustained performance
              </span>
            </li>
            <li>
              <span className="text-xs text-muted-foreground">
                Agents with &gt;85% workload may need support or task redistribution
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentProductivityWorkload;
